import { announce } from "@kovernow/flows/announce";
import {
  addedItemName,
  addItemQueryKeys,
  createDraftStore,
  DRAFT_DEBOUNCE_MS,
  EMPTY_FIELDS,
  fieldsFromDraft,
  guardStep,
  insuredAmountField,
  isAddCategory,
  isCollectorCategory,
  isLegacyCategory,
  parseAddItemParams,
  partnerDeclarationFor,
  stepNumber,
  stepsFor,
  stepTotal,
  toAddItemParams,
  toDraftFields,
  todayIn,
  type AddCategory,
  type AddItemFields,
  type AddItemStep,
  type CollectorValues,
  type CreatedItem,
  type LegacyStep,
} from "@kovernow/flows/add_item";
import { homeQueryKeys, resolveProductTiles } from "@kovernow/flows/home";
import { toRecoveryAction } from "@kovernow/flows/recovery";
import { createUploadQueue } from "@kovernow/flows/uploads";
import { vaultQueryKeys } from "@kovernow/flows/vault";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { Page } from "../../components/Page";
import { Recovery } from "../../components/Recovery";
import { useServices } from "../../core/app-context";
import { safeLocalStorage } from "../../core/services";
import { useT } from "../../core/use-t";
import { CategoryStep } from "./CategoryStep";
import { CollectorDocumentsStep } from "./CollectorDocumentsStep";
import { CollectorSectionStep } from "./CollectorSectionStep";
import { ConditionStep } from "./ConditionStep";
import { DetailsStep } from "./DetailsStep";
import { PhotosStep } from "./PhotosStep";
import { SearchStep } from "./SearchStep";
import type { ServerFieldErrors } from "./shared";

const STEP_TITLE_KEYS: Record<LegacyStep, string> = {
  category: "addItem.category.title",
  search: "addItem.search.title",
  details: "addItem.details.title",
  condition: "addItem.condition.title",
  photos: "addItem.photos.title",
};

/**
 * Add item: steps in the URL (`?category=&step=`), each a pushed history
 * entry so browser Back goes to the previous step. Legacy categories have
 * five steps (category, search, details, condition, photos); a collector
 * has the category step then each schema section (info, grading, storage,
 * documents). A step whose earlier steps are incomplete redirects to the
 * first incomplete one. Text and choice values autosave to a per-user
 * draft; files never do.
 */
export function AddItem() {
  const t = useT();
  const services = useServices();
  const { config, addItemApi, homeApi, format } = services;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const account = useQuery({ queryKey: addItemQueryKeys.account(), queryFn: () => addItemApi.account() });
  const flags = useQuery({ queryKey: homeQueryKeys.flags(), queryFn: () => homeApi.flags(), retry: false });
  const flagsSettled = !flags.isPending;

  const categories = useMemo(
    () =>
      resolveProductTiles(config.productTiles, flags.data)
        .map((tile) => tile.category)
        .filter(isAddCategory),
    [config.productTiles, flags.data],
  );
  const params = parseAddItemParams(searchParams, categories);

  const [fields, setFieldsState] = useState<AddItemFields>(EMPTY_FIELDS);
  const setFields = useCallback((update: (f: AddItemFields) => AddItemFields) => setFieldsState(update), []);
  const [serverErrors, setServerErrors] = useState<ServerFieldErrors>({});
  const [reattach, setReattach] = useState<string[]>([]);
  const [queue] = useState(() => createUploadQueue({ adapter: services.uploads, convertHeic: services.convertHeic }));
  const queueState = useSyncExternalStore(queue.subscribe, queue.getState);
  const submitInFlight = useRef(false);

  // A different category than the form was filled for starts a fresh form.
  const current: AddItemFields =
    params.category === fields.category ? fields : { ...EMPTY_FIELDS, category: params.category };
  useEffect(() => {
    if (params.category && params.category !== fields.category) {
      // Another category's photos and receipt don't carry over.
      if (fields.category) {
        queue.clear();
        setReattach([]);
      }
      setFieldsState({ ...EMPTY_FIELDS, category: params.category });
    }
  }, [params.category, fields.category, queue]);

  const now = new Date();
  const ctx = { now, today: todayIn(config.timeZone, now), currency: config.currency };

  // ------------------------------------------------------------ drafts
  const userId = account.data?.id ?? null;
  const drafts = useMemo(() => (userId ? createDraftStore(safeLocalStorage(), userId) : null), [userId]);
  const [restored, setRestored] = useState(false);
  useEffect(() => {
    if (!drafts || restored || !flagsSettled) return;
    setRestored(true);
    const draft = drafts.load();
    if (!draft || !draft.category || !categories.includes(draft.category)) return;
    if (params.category && params.category !== draft.category) return;
    setFieldsState(fieldsFromDraft(draft));
    setReattach(draft.fileNames ?? []);
    if (!params.category) {
      navigate({ search: toAddItemParams({ category: draft.category, step: draft.step }).toString() }, { replace: true });
    }
  }, [drafts, restored, flagsSettled, categories, params.category, navigate]);

  const fileNames = useMemo(
    () => queueState.files.filter((f) => f.status !== "invalid").map((f) => f.name),
    [queueState.files],
  );
  // Text fields (and the chosen files' names) only; debounced.
  const draftJson = current.category
    ? JSON.stringify({
        category: current.category,
        step: params.step,
        fields: toDraftFields(current),
        fileNames: [...new Set([...fileNames, ...reattach])],
      })
    : null;
  useEffect(() => {
    if (!drafts || !restored || !draftJson) return;
    const id = setTimeout(() => drafts.save(JSON.parse(draftJson)), DRAFT_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [drafts, restored, draftJson]);

  // ------------------------------------------------------------ navigation
  const goTo = useCallback(
    (step: AddItemStep, category?: AddCategory | null, opts?: { replace?: boolean }) => {
      const c = category === undefined ? params.category : category;
      navigate({ search: toAddItemParams({ category: c, step }).toString() }, { replace: opts?.replace });
    },
    [navigate, params.category],
  );
  const back = useCallback(() => {
    const steps = stepsFor(params.category);
    const i = steps.indexOf(params.step);
    const prev = steps[Math.max(0, i - 1)]!;
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate({ search: toAddItemParams({ category: prev === "category" ? null : params.category, step: prev }).toString() }, { replace: true });
  }, [navigate, params.category, params.step]);

  const ready = flagsSettled && !!account.data && (restored || !drafts);
  const step = ready ? guardStep(params.step, current, ctx) : params.step;
  useEffect(() => {
    if (!ready || step === params.step) return;
    navigate({ search: toAddItemParams({ category: params.category, step }).toString() }, { replace: true });
  }, [ready, step, params.step, params.category, navigate]);

  // Focus the step's h2 on every step change (not on first load).
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const lastStep = useRef<AddItemStep | null>(location.key === "default" ? step : null);
  useEffect(() => {
    if (!ready || lastStep.current === step) return;
    lastStep.current = step;
    headingRef.current?.focus();
  });

  const declaration = partnerDeclarationFor(config.partnerDeclarations, account.data?.partnerId, current.category);

  const collector = isCollectorCategory(current.category) ? current.category : null;
  const stepTitle =
    step === "search" && current.category
      ? t(STEP_TITLE_KEYS.search, { category: t(`addItem.noun.${current.category}`) })
      : step in STEP_TITLE_KEYS
        ? t(STEP_TITLE_KEYS[step as LegacyStep])
        : t(`addItem.steps.${step}`);
  const stepOf = t("addItem.stepOf", {
    n: format.number(stepNumber(step, current.category)),
    total: format.number(stepTotal(current.category)),
  });

  const page = (body: ReactNode) => (
    <Page titleKey="addItem.docTitle" titleValues={{ stepOf, step: stepTitle }} flowKey="nav.addItem" headingKey="nav.addItem" focusKey={null}>
      {body}
    </Page>
  );

  if (account.isError) {
    return page(
      <div data-testid="add-item-load-error">
        <Recovery action={toRecoveryAction(account.error)} onRetry={() => void account.refetch()} retryable />
      </div>,
    );
  }
  if (!ready) {
    return page(
      <p aria-busy="true" className="text-muted-foreground">
        {t("common.loading")}
      </p>,
    );
  }

  const onFieldError = (errStep: AddItemStep, field: string, messageKey: string) => {
    // `insuredAmount` is whichever field holds the insured amount.
    const key = field === "insuredAmount" ? insuredAmountField(current.attributes) : field;
    setServerErrors((s) => ({ ...s, [key]: messageKey }));
    // Replace: Back from the field must not land on a step the guard bounces.
    if (errStep !== "photos" && errStep !== "documents") goTo(errStep, undefined, { replace: true });
  };
  const onCreated = (created: CreatedItem) => {
    const name = addedItemName(current);
    drafts?.clear();
    queue.clear();
    void queryClient.invalidateQueries({ queryKey: vaultQueryKeys.items() });
    void queryClient.invalidateQueries({ queryKey: homeQueryKeys.dashboard() });
    if (created.status === "pending_underwriter_review") {
      announce(name ? t("addItem.addedReview", { name }) : t("addItem.addedReviewFallback"));
    } else {
      announce(name ? t("addItem.added", { name }) : t("addItem.addedFallback"));
    }
    navigate("/vault");
  };
  const setAttributes = (update: (v: CollectorValues) => CollectorValues) =>
    setFields((f) => ({ ...f, attributes: update(f.attributes) }));

  const stepProps = {
    fields: current,
    setFields,
    goTo,
    back,
    headingRef,
    serverErrors,
    clearServerError: (f: string) =>
      setServerErrors((s) => {
        if (!s[f]) return s;
        const next = { ...s };
        delete next[f];
        return next;
      }),
  };

  return page(
    <div data-testid="add-item" data-step={step} className="flex flex-col gap-4">
      <p className="text-sm font-semibold text-muted-foreground" data-testid="step-of">
        {stepOf}
      </p>
      {step === "category" || !current.category ? (
        <CategoryStep categories={categories} goTo={goTo} headingRef={headingRef} />
      ) : collector ? (
        step === "documents" ? (
          <CollectorDocumentsStep
            category={collector}
            values={current.attributes}
            today={ctx.today}
            back={back}
            headingRef={headingRef}
            serverErrors={serverErrors}
            clearServerError={stepProps.clearServerError}
            queue={queue}
            ownerId={userId}
            reattach={reattach}
            inFlight={submitInFlight}
            onFieldError={onFieldError}
            onCreated={onCreated}
          />
        ) : (
          <CollectorSectionStep
            key={step}
            category={collector}
            sectionId={step as "info" | "grading" | "storage"}
            values={current.attributes}
            setValues={setAttributes}
            ctx={ctx}
            headingRef={headingRef}
            serverErrors={serverErrors}
            clearServerError={stepProps.clearServerError}
            onBack={back}
            onContinue={() => {
              const steps = stepsFor(collector);
              goTo(steps[steps.indexOf(step) + 1]!);
            }}
          />
        )
      ) : step === "search" && isLegacyCategory(current.category) ? (
        <SearchStep category={current.category} setFields={setFields} goTo={goTo} back={back} headingRef={headingRef} />
      ) : step === "details" ? (
        <DetailsStep {...stepProps} ctx={ctx} declaration={declaration} />
      ) : step === "condition" ? (
        <ConditionStep {...stepProps} />
      ) : (
        <PhotosStep
          {...stepProps}
          queue={queue}
          ownerId={userId}
          reattach={reattach}
          declarationShown={!!declaration}
          inFlight={submitInFlight}
          onFieldError={onFieldError}
          onCreated={onCreated}
        />
      )}
    </div>,
  );
}
